import React, { useState, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Upload,
  Download,
  BarChart3,
  Clock,
  Calendar as CalendarIcon,
  Users,
  TrendingUp,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Settings,
  X,
  Loader2,
  HelpCircle,
  Lock,
  FileText,
  TrendingDown,
  Timer
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { el } from 'date-fns/locale';
import { getBackendUrl } from '../utils/api';

// Daypart configuration
const DAYPART_CONFIG = {
  overnight: { name: 'Overnight', nameEl: 'Νύχτα', hours: '00:00-06:00', emoji: '🌃', defaultWeight: 0.8 },
  morning: { name: 'Morning', nameEl: 'Πρωί', hours: '06:00-10:00', emoji: '☀️', defaultWeight: 1.5 },
  daytime: { name: 'Daytime', nameEl: 'Μεσημέρι', hours: '10:00-15:00', emoji: '🌤️', defaultWeight: 1.3 },
  afternoon: { name: 'Afternoon', nameEl: 'Απόγευμα', hours: '15:00-19:00', emoji: '🚗', defaultWeight: 1.2 },
  evening: { name: 'Evening', nameEl: 'Βράδυ', hours: '19:00-24:00', emoji: '🌙', defaultWeight: 1.0 }
};

// Weekday configuration
const WEEKDAY_CONFIG = {
  0: { name: 'Monday', nameEl: 'Δευτέρα', short: 'Δευ', defaultWeight: 1.2 },
  1: { name: 'Tuesday', nameEl: 'Τρίτη', short: 'Τρι', defaultWeight: 1.2 },
  2: { name: 'Wednesday', nameEl: 'Τετάρτη', short: 'Τετ', defaultWeight: 1.2 },
  3: { name: 'Thursday', nameEl: 'Πέμπτη', short: 'Πεμ', defaultWeight: 1.2 },
  4: { name: 'Friday', nameEl: 'Παρασκευή', short: 'Παρ', defaultWeight: 1.3 },
  5: { name: 'Saturday', nameEl: 'Σάββατο', short: 'Σαβ', defaultWeight: 1.0 },
  6: { name: 'Sunday', nameEl: 'Κυριακή', short: 'Κυρ', defaultWeight: 0.9 }
};

// Stat Card Component
const StatCard = ({ icon, label, value, color, subValue }) => (
  <div className="p-4 bg-gray-800/50 rounded-xl border border-gray-700/50">
    <div className={`${color} mb-2`}>{icon}</div>
    <p className="text-gray-300 text-sm">{label}</p>
    <p className={`text-2xl font-bold ${color}`}>{value}</p>
    {subValue && <p className="text-xs text-gray-400 mt-1">{subValue}</p>}
  </div>
);

// Heatmap color function - Red (high) → Green (medium) → Gray → Black (low)
const getHeatmapColor = (intensity) => {
  if (intensity <= 0) {
    // No data - almost black
    return 'rgb(20, 20, 25)';
  } else if (intensity < 0.25) {
    // Low - dark gray to gray
    const r = Math.round(20 + intensity * 4 * 60);
    const g = Math.round(20 + intensity * 4 * 60);
    const b = Math.round(25 + intensity * 4 * 60);
    return `rgb(${r}, ${g}, ${b})`;
  } else if (intensity < 0.5) {
    // Medium-low - gray to green
    const factor = (intensity - 0.25) * 4;
    const r = Math.round(80 - factor * 40);
    const g = Math.round(80 + factor * 100);
    const b = Math.round(85 - factor * 45);
    return `rgb(${r}, ${g}, ${b})`;
  } else if (intensity < 0.75) {
    // Medium-high - green to yellow/orange
    const factor = (intensity - 0.5) * 4;
    const r = Math.round(40 + factor * 200);
    const g = Math.round(180 - factor * 50);
    const b = Math.round(40 - factor * 20);
    return `rgb(${r}, ${g}, ${b})`;
  } else {
    // High - orange to red
    const factor = (intensity - 0.75) * 4;
    const r = Math.round(240 + factor * 15);
    const g = Math.round(130 - factor * 100);
    const b = Math.round(20 - factor * 10);
    return `rgb(${r}, ${g}, ${b})`;
  }
};

// Trend Badge Component
const TrendBadge = ({ value, label }) => {
  const isPositive = value > 0;
  const isNeutral = value === 0;
  
  return (
    <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
      isNeutral ? 'bg-gray-700 text-gray-300' :
      isPositive ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
    }`}>
      {!isNeutral && (isPositive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />)}
      <span>{isPositive ? '+' : ''}{value?.toFixed(1)}%</span>
      <span className="text-gray-400 ml-1">{label}</span>
    </div>
  );
};

// Instructions Modal
const InstructionsModal = ({ open, onOpenChange }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-2xl bg-gray-900 border-purple-500/30 text-white">
      <DialogHeader>
        <DialogTitle className="text-2xl text-amber-400 flex items-center gap-2">
          <HelpCircle className="w-6 h-6" />
          Οδηγίες Χρήσης
        </DialogTitle>
        <DialogDescription className="text-gray-300">
          Πώς να χρησιμοποιήσετε το εργαλείο
        </DialogDescription>
      </DialogHeader>
      
      <div className="space-y-4 mt-4 max-h-[60vh] overflow-y-auto pr-2">
        <div className="p-4 bg-purple-900/30 rounded-lg border border-purple-500/30">
          <h3 className="font-bold text-purple-300 mb-2">📁 Βήμα 1: Ανέβασμα CSV</h3>
          <p className="text-gray-300 text-sm mb-2">
            Ανεβάστε 1 ή 2 αρχεία CSV από τον streaming provider σας.
          </p>
          <div className="text-xs text-gray-400 space-y-1">
            <p><span className="text-amber-400">• Ένα αρχείο:</span> Απλή ανάλυση listeners ή minutes</p>
            <p><span className="text-green-400">• Δύο αρχεία:</span> Συνδυαστική ανάλυση με μέση διάρκεια session, trends κ.α.</p>
          </div>
        </div>

        <div className="p-4 bg-blue-900/30 rounded-lg border border-blue-500/30">
          <h3 className="font-bold text-blue-300 mb-2">📅 Βήμα 2: Επιλογή Περιόδου</h3>
          <p className="text-gray-300 text-sm">
            Επιλέξτε ημερομηνίες (από-έως), ημέρες εβδομάδας και ώρες.
          </p>
        </div>

        <div className="p-4 bg-green-900/30 rounded-lg border border-green-500/30">
          <h3 className="font-bold text-green-300 mb-2">📊 Βήμα 3: Ανάλυση</h3>
          <p className="text-gray-300 text-sm">
            Πατήστε ΑΝΑΛΥΣΗ για να δείτε τα αποτελέσματα.
          </p>
        </div>

        <div className="p-4 bg-amber-900/30 rounded-lg border border-amber-500/30">
          <h3 className="font-bold text-amber-300 mb-2">🔗 Συνδυαστική Ανάλυση (2 αρχεία)</h3>
          <p className="text-gray-300 text-sm mb-2">
            Με 2 αρχεία (listeners + minutes) αποκτάτε:
          </p>
          <ul className="text-xs text-gray-400 space-y-1 ml-4 list-disc">
            <li>Μέση διάρκεια session ανά ώρα</li>
            <li>Trends τάσης (7/14/30 ημέρες)</li>
            <li>Heatmap ακροαματικότητας</li>
            <li>Engagement Score</li>
          </ul>
        </div>

        <div className="p-4 bg-gray-800 rounded-lg border border-gray-600">
          <h3 className="font-bold text-white mb-2">📚 Επεξήγηση Μετρήσεων</h3>
          <div className="text-xs space-y-2">
            <div>
              <span className="text-amber-400 font-semibold">Μέσος Ταυτόχρονων:</span>
              <p className="text-gray-400">Μέσος αριθμός ακροατών που ακούνε ταυτόχρονα</p>
            </div>
            <div>
              <span className="text-amber-400 font-semibold">TLH (Ώρες Ακρόασης):</span>
              <p className="text-gray-400">= Μέσος Ταυτόχρονων × Επιλεγμένες Ώρες</p>
            </div>
            <div>
              <span className="text-amber-400 font-semibold">Μέση Διάρκεια Session:</span>
              <p className="text-gray-400">= Λεπτά Ακρόασης / Ακροατές (απαιτεί 2 αρχεία)</p>
            </div>
            <div>
              <span className="text-amber-400 font-semibold">Κορυφαία Ώρα:</span>
              <p className="text-gray-400">Η ώρα με τους περισσότερους ακροατές</p>
            </div>
          </div>
        </div>
      </div>

      <DialogFooter>
        <Button onClick={() => onOpenChange(false)} className="bg-purple-600 hover:bg-purple-700">
          Κατάλαβα!
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

const StreamingAnalytics = () => {
  const { currentLang } = useLanguage();
  const fileInputRef = useRef(null);
  
  // State - Now supporting multiple files
  const [files, setFiles] = useState([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [error, setError] = useState(null);
  const [showInstructions, setShowInstructions] = useState(false);
  
  // Date Range State
  const [dateRange, setDateRange] = useState({ from: undefined, to: undefined });
  
  // Filters state
  const [selectedDays, setSelectedDays] = useState([]);
  const [selectedHours, setSelectedHours] = useState([]);
  const [selectedDayparts, setSelectedDayparts] = useState(['overnight', 'morning', 'daytime', 'afternoon', 'evening']);
  
  // Weights state
  const [showWeights, setShowWeights] = useState(false);
  const [daypartWeights, setDaypartWeights] = useState({
    overnight: 0.8, morning: 1.5, daytime: 1.3, afternoon: 1.2, evening: 1.0
  });
  const [weekdayWeights, setWeekdayWeights] = useState({
    0: 1.2, 1: 1.2, 2: 1.2, 3: 1.2, 4: 1.3, 5: 1.0, 6: 0.9
  });

  // Check if filters are valid
  const hasValidFilters = dateRange.from && dateRange.to && selectedDays.length > 0;
  const hasFiles = files.length > 0;
  const isCombinedAnalysis = files.length === 2;

  // Get username for PDF password
  const getUsername = () => {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        return user.username || 'radio';
      }
    } catch (e) {
      console.warn('Could not get username:', e);
    }
    return 'radio';
  };

  // Analyze function - supports single and combined analysis
  const doAnalyze = useCallback(async () => {
    if (!hasFiles) {
      toast.error('Παρακαλώ επιλέξτε αρχείο/αρχεία CSV');
      return;
    }

    if (!hasValidFilters) {
      toast.error('Παρακαλώ επιλέξτε περίοδο, ημέρες και ώρες');
      return;
    }

    setIsAnalyzing(true);
    setError(null);

    try {
      const formData = new FormData();
      
      // Add files - use different endpoint based on file count
      // Use getBackendUrl() dynamically at runtime to ensure correct domain in production
      const backendUrl = getBackendUrl();
      const endpoint = isCombinedAnalysis 
        ? `${backendUrl}/api/streaming-analytics/analyze-combined`
        : `${backendUrl}/api/streaming-analytics/analyze`;
      
      if (isCombinedAnalysis) {
        // Multiple files for combined analysis
        files.forEach(f => formData.append('files', f));
      } else {
        // Single file
        formData.append('file', files[0]);
      }
      
      formData.append('daypart_weights', JSON.stringify(daypartWeights));
      formData.append('weekday_weights', JSON.stringify(weekdayWeights));
      formData.append('date_from', format(dateRange.from, 'yyyy-MM-dd'));
      formData.append('date_to', format(dateRange.to, 'yyyy-MM-dd'));
      formData.append('selected_days', JSON.stringify(selectedDays));
      formData.append('selected_hours', JSON.stringify(selectedHours));
      formData.append('selected_dayparts', JSON.stringify(selectedDayparts));

      const response = await fetch(endpoint, {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });

      if (!response.ok) {
        let errorMessage = 'Η ανάλυση απέτυχε';
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorMessage;
        } catch {
          // If response is not JSON, use status text
          errorMessage = `Σφάλμα ${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      setAnalysisResult(result);
      toast.success(isCombinedAnalysis ? 'Συνδυαστική ανάλυση ολοκληρώθηκε!' : 'Ανάλυση ολοκληρώθηκε!');
    } catch (err) {
      // Better error messages for common issues
      let errorMsg = err.message;
      if (err.message === 'Failed to fetch') {
        errorMsg = 'Αποτυχία σύνδεσης. Ελέγξτε τη σύνδεσή σας και δοκιμάστε ξανά.';
      } else if (err.message.includes('timeout')) {
        errorMsg = 'Η ανάλυση έληξε χρονικά. Δοκιμάστε με μικρότερα αρχεία.';
      }
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setIsAnalyzing(false);
    }
  }, [files, hasFiles, hasValidFilters, isCombinedAnalysis, daypartWeights, weekdayWeights, dateRange, selectedDays, selectedHours, selectedDayparts]);

  // File upload handler - now supports multiple files
  const handleFileChange = useCallback((e) => {
    const selectedFiles = Array.from(e.target.files || []);
    
    if (selectedFiles.length === 0) return;
    
    if (selectedFiles.length > 2) {
      toast.error('Μέγιστο 2 αρχεία επιτρέπονται');
      return;
    }
    
    // Validate all files are CSV
    const invalidFiles = selectedFiles.filter(f => !f.name.endsWith('.csv'));
    if (invalidFiles.length > 0) {
      toast.error('Παρακαλώ επιλέξτε μόνο αρχεία CSV');
      return;
    }
    
    setFiles(selectedFiles);
    setError(null);
    setAnalysisResult(null);
    
    if (selectedFiles.length === 1) {
      toast.success(`Αρχείο "${selectedFiles[0].name}" επιλέχθηκε`);
    } else {
      toast.success(`${selectedFiles.length} αρχεία επιλέχθηκαν για συνδυαστική ανάλυση`);
    }
  }, []);

  // Remove a file
  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
    setAnalysisResult(null);
  };

  // Toggle helpers
  const toggleDay = (day) => {
    setSelectedDays(prev => 
      prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]
    );
  };

  const toggleHour = (hour) => {
    setSelectedHours(prev => 
      prev.includes(hour) ? prev.filter(h => h !== hour) : [...prev, hour]
    );
  };

  const toggleAllDays = () => {
    if (selectedDays.length === 7) {
      setSelectedDays([]);
    } else {
      setSelectedDays([0, 1, 2, 3, 4, 5, 6]);
    }
  };

  const toggleAllHours = () => {
    if (selectedHours.length === 24) {
      setSelectedHours([]);
    } else {
      setSelectedHours(Array.from({length: 24}, (_, i) => i));
    }
  };

  // Export PDF
  const handleExportPDF = async () => {
    if (!analysisResult) return;
    
    const username = getUsername();
    const loadingToast = toast.loading('Δημιουργία PDF...');
    
    try {
      const response = await fetch(`${getBackendUrl()}/api/streaming-analytics/export-pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          analysis_id: analysisResult.analysis_id,
          username: username
        }),
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Αποτυχία εξαγωγής');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `streaming_analysis_${format(new Date(), 'yyyy-MM-dd')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      
      toast.dismiss(loadingToast);
      toast.success('PDF εξήχθη επιτυχώς! 🔐 Το PDF θα κλειδωθεί με το όνομα χρήστη σας', { duration: 8000 });
    } catch (err) {
      toast.dismiss(loadingToast);
      toast.error('Σφάλμα εξαγωγής PDF');
    }
  };

  return (
    <div className="space-y-6">
      <InstructionsModal open={showInstructions} onOpenChange={setShowInstructions} />
      
      {/* Upload Section */}
      <Card className="border-2 border-amber-500/30 bg-gradient-to-br from-[#2D1B4E]/80 to-[#1a1625]/80">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-amber-400">
              <BarChart3 className="w-6 h-6" />
              Αναλυτής Streaming Δεδομένων
            </CardTitle>
            <Button
              onClick={() => setShowInstructions(true)}
              variant="outline"
              className="bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 border-amber-500/50"
            >
              <HelpCircle className="w-5 h-5 mr-1" />
              <span className="hidden sm:inline">Οδηγίες</span>
            </Button>
          </div>
          <CardDescription className="text-gray-300">
            Αναλύστε τα δεδομένα streaming του σταθμού σας
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          
          {/* File Upload */}
          <div>
            <h3 className="text-lg font-bold mb-3 flex items-center gap-2 text-amber-400">
              <Upload className="h-5 w-5" />
              Ανέβασμα CSV
              <span className="text-xs text-gray-400 font-normal ml-2">(1-2 αρχεία)</span>
            </h3>
            
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              multiple
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
            
            <div className="flex flex-wrap gap-3 items-start">
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={isAnalyzing}
                className="px-6 py-3 bg-gradient-to-r from-purple-600 to-purple-800 text-white rounded-lg font-bold flex items-center gap-2 hover:from-purple-700 hover:to-purple-900 disabled:opacity-50"
              >
                <Upload size={16} />
                {files.length > 0 ? 'Αλλαγή αρχείων' : 'Επιλογή CSV'}
              </button>
              
              {files.length === 0 && (
                <p className="text-xs text-gray-400 self-center">
                  Κρατήστε Ctrl/Cmd για επιλογή 2 αρχείων
                </p>
              )}
            </div>
            
            {/* Selected files display */}
            {files.length > 0 && (
              <div className="mt-3 space-y-2">
                {files.map((f, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-sm bg-gray-800/50 rounded-lg px-3 py-2">
                    <FileText className="w-4 h-4 text-purple-400" />
                    <span className="text-green-400">{f.name}</span>
                    <span className="text-gray-500">({(f.size / 1024).toFixed(1)} KB)</span>
                    <button 
                      onClick={() => removeFile(idx)}
                      className="ml-auto text-red-400 hover:text-red-300"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                
                {/* Analysis mode indicator */}
                <div className={`flex items-center gap-2 text-xs px-3 py-2 rounded-lg ${
                  isCombinedAnalysis 
                    ? 'bg-green-500/10 border border-green-500/30 text-green-400' 
                    : 'bg-blue-500/10 border border-blue-500/30 text-blue-400'
                }`}>
                  {isCombinedAnalysis ? (
                    <>
                      <TrendingUp className="w-4 h-4" />
                      <span>Συνδυαστική Ανάλυση: Μέση διάρκεια session, Trends, Heatmap</span>
                    </>
                  ) : (
                    <>
                      <BarChart3 className="w-4 h-4" />
                      <span>Απλή Ανάλυση: Προσθέστε 2ο αρχείο για περισσότερες μετρικές</span>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Filters */}
          <div className="space-y-4 p-4 bg-purple-500/10 rounded-xl border border-purple-500/20">
            <h3 className="text-purple-300 font-semibold flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Επιλογή Φίλτρων
              <span className="text-xs text-amber-400 ml-2">(υποχρεωτικά)</span>
            </h3>
            
            {/* Date Range Picker */}
            <div>
              <Label className="text-amber-400 text-sm mb-2 block">
                📅 Περίοδος Ημερομηνιών
              </Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left font-normal bg-gray-800/50 border-purple-500/30 text-white hover:bg-gray-700/50"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {dateRange.from ? (
                      dateRange.to ? (
                        <>
                          {format(dateRange.from, "dd/MM/yyyy", { locale: el })} - {format(dateRange.to, "dd/MM/yyyy", { locale: el })}
                        </>
                      ) : (
                        format(dateRange.from, "dd/MM/yyyy", { locale: el })
                      )
                    ) : (
                      <span className="text-gray-400">Επιλέξτε ημερομηνίες...</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-gray-900 border-purple-500/30" align="start">
                  <Calendar
                    initialFocus
                    mode="range"
                    defaultMonth={dateRange.from}
                    selected={dateRange}
                    onSelect={setDateRange}
                    numberOfMonths={2}
                    locale={el}
                    className="text-white"
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Day Selector */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-amber-400 text-sm">Ημέρες Εβδομάδας</Label>
                <label className="flex items-center gap-2 text-xs text-gray-400 cursor-pointer">
                  <Checkbox 
                    checked={selectedDays.length === 7}
                    onCheckedChange={toggleAllDays}
                    className="border-purple-500 data-[state=checked]:bg-purple-600"
                  />
                  Επιλογή Όλων
                </label>
              </div>
              <div className="flex gap-2 flex-wrap">
                {Object.entries(WEEKDAY_CONFIG).map(([key, config]) => (
                  <button
                    key={key}
                    onClick={() => toggleDay(parseInt(key))}
                    className={`px-3 py-2 rounded-lg font-semibold transition-all text-sm ${
                      selectedDays.includes(parseInt(key))
                        ? 'bg-purple-500 text-white ring-2 ring-purple-300'
                        : 'bg-gray-700/50 text-gray-400 hover:bg-gray-600/50'
                    }`}
                  >
                    {config.nameEl}
                  </button>
                ))}
              </div>
            </div>

            {/* Hour Selector */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-amber-400 text-sm">Ώρες</Label>
                <label className="flex items-center gap-2 text-xs text-gray-400 cursor-pointer">
                  <Checkbox 
                    checked={selectedHours.length === 24}
                    onCheckedChange={toggleAllHours}
                    className="border-purple-500 data-[state=checked]:bg-purple-600"
                  />
                  Επιλογή Όλων
                </label>
              </div>
              <div className="grid grid-cols-12 gap-1">
                {Array.from({length: 24}, (_, i) => (
                  <button
                    key={i}
                    onClick={() => toggleHour(i)}
                    className={`p-2 rounded text-xs font-mono transition-all ${
                      selectedHours.includes(i)
                        ? 'bg-green-500 text-white ring-1 ring-green-300'
                        : 'bg-gray-700/50 text-gray-400 hover:bg-gray-600/50'
                    }`}
                  >
                    {i.toString().padStart(2, '0')}
                  </button>
                ))}
              </div>
              {selectedHours.length > 0 && (
                <p className="text-xs text-gray-400 mt-1">
                  Επιλεγμένες: {selectedHours.length} ώρες
                </p>
              )}
            </div>

            {/* Validation message */}
            {!hasValidFilters && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-red-400 text-sm flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                Επιλέξτε περίοδο και ημέρες για να συνεχίσετε (οι ώρες είναι προαιρετικές)
              </div>
            )}
          </div>

          {/* Weights Toggle */}
          <div className="border border-purple-500/20 rounded-lg overflow-hidden">
            <button
              className="w-full flex items-center justify-between p-4 bg-purple-500/10 hover:bg-purple-500/20 transition-colors"
              onClick={() => setShowWeights(!showWeights)}
            >
              <span className="flex items-center gap-2 text-purple-300 font-semibold">
                <Settings className="w-5 h-5" />
                Ρυθμίσεις Βάρους (προαιρετικά)
              </span>
              {showWeights ? <ChevronUp className="w-5 h-5 text-purple-400" /> : <ChevronDown className="w-5 h-5 text-purple-400" />}
            </button>
            
            {showWeights && (
              <div className="p-4 space-y-4 bg-purple-900/20">
                <div>
                  <Label className="text-amber-400 text-sm font-semibold mb-2 block">
                    Βάρος ανά Χρονική Ζώνη
                  </Label>
                  <div className="grid grid-cols-5 gap-2">
                    {Object.entries(DAYPART_CONFIG).map(([key, config]) => (
                      <div key={key} className="text-center">
                        <span className="text-2xl">{config.emoji}</span>
                        <p className="text-xs text-gray-400 mb-1">{config.nameEl}</p>
                        <Input
                          type="number"
                          step="0.1"
                          min="0"
                          max="3"
                          value={daypartWeights[key]}
                          onChange={(e) => setDaypartWeights(prev => ({...prev, [key]: parseFloat(e.target.value) || 1}))}
                          className="bg-purple-900/50 border-purple-500/30 text-white text-center text-sm h-8"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Analyze Button */}
          <Button
            onClick={doAnalyze}
            disabled={!hasFiles || isAnalyzing || !hasValidFilters}
            className="w-full bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white font-bold py-3 disabled:opacity-50"
          >
            {isAnalyzing ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                {isCombinedAnalysis ? 'Συνδυαστική Ανάλυση...' : 'Ανάλυση...'}
              </>
            ) : (
              <>
                <BarChart3 className="w-5 h-5 mr-2" />
                {isCombinedAnalysis ? 'ΣΥΝΔΥΑΣΤΙΚΗ ΑΝΑΛΥΣΗ' : 'ΑΝΑΛΥΣΗ ΔΕΔΟΜΕΝΩΝ'}
              </>
            )}
          </Button>

          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-red-400">
              <AlertCircle className="w-5 h-5 inline mr-2" />
              {error}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Results Section */}
      {analysisResult && hasValidFilters && (
        <Card className="border-2 border-green-500/30 bg-gradient-to-br from-[#2D1B4E]/80 to-[#1a1625]/80">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2 text-green-400">
                  <TrendingUp className="w-6 h-6" />
                  Αποτελέσματα Ανάλυσης
                  {analysisResult.analysis_type === 'combined' && (
                    <span className="text-xs bg-green-500/20 text-green-400 px-2 py-1 rounded-full ml-2">
                      Συνδυαστική
                    </span>
                  )}
                </CardTitle>
                <CardDescription className="text-gray-300 mt-1">
                  Περίοδος: {analysisResult.period?.start} - {analysisResult.period?.end}
                </CardDescription>
              </div>
              <div className="flex flex-col items-end gap-1">
                <Button
                  onClick={handleExportPDF}
                  variant="outline"
                  className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
                >
                  <Download className="w-4 h-4 mr-2" />
                  <Lock className="w-3 h-3 mr-1" />
                  PDF
                </Button>
                <span className="text-xs text-gray-400">🔐 Το PDF κλειδώνεται με το username σας</span>
              </div>
            </div>
            
            {/* Trends Section - Only for combined analysis */}
            {analysisResult.trends && (
              <div className="mt-4 flex flex-wrap gap-2">
                <span className="text-xs text-gray-400 self-center mr-2">Τάσεις Ακροατών:</span>
                <TrendBadge value={analysisResult.trends?.listeners?.trend_7d} label="7 ημ" />
                <TrendBadge value={analysisResult.trends?.listeners?.trend_14d} label="14 ημ" />
                <TrendBadge value={analysisResult.trends?.listeners?.trend_30d} label="30 ημ" />
              </div>
            )}
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="summary" className="w-full">
              <TabsList className={`grid w-full bg-purple-900/30 ${analysisResult.analysis_type === 'combined' ? 'grid-cols-5' : 'grid-cols-4'}`}>
                <TabsTrigger value="summary" className="data-[state=active]:bg-purple-600">Σύνοψη</TabsTrigger>
                <TabsTrigger value="hourly" className="data-[state=active]:bg-purple-600">Ανά Ώρα</TabsTrigger>
                <TabsTrigger value="daypart" className="data-[state=active]:bg-purple-600">Ζώνες</TabsTrigger>
                <TabsTrigger value="daily" className="data-[state=active]:bg-purple-600">Ανά Ημέρα</TabsTrigger>
                {analysisResult.analysis_type === 'combined' && (
                  <TabsTrigger value="heatmap" className="data-[state=active]:bg-purple-600">Heatmap</TabsTrigger>
                )}
              </TabsList>

              {/* Summary Tab */}
              <TabsContent value="summary" className="mt-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <StatCard
                    icon={<Users className="w-6 h-6" />}
                    label="Μέσος Όρος Ταυτόχρονων Ακροατών"
                    value={analysisResult.summary?.avg_concurrent?.toFixed(1) || '0'}
                    color="text-blue-400"
                  />
                  <StatCard
                    icon={<Users className="w-6 h-6" />}
                    label="Μέγιστος Ταυτόχρονων Ακροατών"
                    value={analysisResult.summary?.max_concurrent?.toFixed(0) || '0'}
                    color="text-green-400"
                  />
                  <StatCard
                    icon={<Clock className="w-6 h-6" />}
                    label="Συνολικές Ώρες Ακρόασης (TLH) ανά Ημέρα"
                    value={`${analysisResult.summary?.tlh_per_day?.toFixed(1) || '0'} ώρες`}
                    color="text-purple-400"
                  />
                  <StatCard
                    icon={<BarChart3 className="w-6 h-6" />}
                    label="Κορυφαία Ώρα Ακρόασης"
                    value={`${analysisResult.summary?.peak_hour || '0'}:00`}
                    color="text-red-400"
                  />
                  
                  {/* Additional metrics for combined analysis */}
                  {analysisResult.analysis_type === 'combined' && analysisResult.has_minutes_data && (
                    <>
                      <StatCard
                        icon={<Timer className="w-6 h-6" />}
                        label="Μέση Διάρκεια Session"
                        value={`${analysisResult.summary?.avg_session_duration?.toFixed(1) || '0'} λεπτά`}
                        color="text-amber-400"
                        subValue="minutes / listeners"
                      />
                      <StatCard
                        icon={<Clock className="w-6 h-6" />}
                        label="Συνολικά Λεπτά Ακρόασης"
                        value={analysisResult.summary?.total_minutes?.toLocaleString() || '0'}
                        color="text-cyan-400"
                      />
                    </>
                  )}
                </div>
                
                {/* Info box */}
                <div className="mt-4 p-3 bg-gray-800/50 rounded-lg border border-gray-700/50 text-xs text-gray-400">
                  <p><strong className="text-amber-400">Υπολογισμός TLH:</strong> Μέσος Ταυτόχρονων × Επιλεγμένες Ώρες = {analysisResult.summary?.avg_concurrent?.toFixed(1)} × {selectedHours.length} = {analysisResult.summary?.tlh_per_day?.toFixed(1)} ώρες</p>
                  <p className="mt-1"><strong className="text-amber-400">Ημέρες που αναλύθηκαν:</strong> {analysisResult.summary?.total_days || 0}</p>
                  {analysisResult.analysis_type === 'combined' && analysisResult.has_minutes_data && (
                    <p className="mt-1"><strong className="text-green-400">Μέση Διάρκεια Session:</strong> Συνολικά Λεπτά / Ακροατές = {analysisResult.summary?.avg_session_duration?.toFixed(1)} λεπτά</p>
                  )}
                  {analysisResult.files_detected && (
                    <p className="mt-1"><strong className="text-purple-400">Αρχεία:</strong> {Object.entries(analysisResult.files_detected).map(([k, v]) => `${k}: ${v}`).join(', ')}</p>
                  )}
                </div>
              </TabsContent>

              {/* Hourly Tab */}
              <TabsContent value="hourly" className="mt-4">
                <div className="space-y-1">
                  {analysisResult.hourly?.map((hour, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <span className="w-12 text-gray-400 text-sm font-mono">{hour.hour.toString().padStart(2, '0')}:00</span>
                      <div className="flex-1 h-6 bg-gray-800 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-purple-500 to-amber-500 rounded-full"
                          style={{ width: `${(hour.avg_listeners / (analysisResult.summary?.max_concurrent || 1)) * 100}%` }}
                        />
                      </div>
                      <span className="w-16 text-right text-white font-semibold">{hour.avg_listeners?.toFixed(1)}</span>
                      {analysisResult.analysis_type === 'combined' && hour.avg_session_duration > 0 && (
                        <span className="w-20 text-right text-amber-400 text-xs">{hour.avg_session_duration?.toFixed(1)} min</span>
                      )}
                    </div>
                  ))}
                </div>
              </TabsContent>

              {/* Daypart Tab */}
              <TabsContent value="daypart" className="mt-4">
                <div className="space-y-4">
                  {analysisResult.dayparts?.map((dp, idx) => {
                    const config = DAYPART_CONFIG[dp.name.toLowerCase()] || DAYPART_CONFIG.daytime;
                    return (
                      <div key={idx} className="flex items-center gap-4 p-3 bg-gray-800/50 rounded-lg">
                        <span className="text-3xl">{config.emoji}</span>
                        <div className="flex-1">
                          <p className="text-white font-semibold">{config.nameEl}</p>
                          <p className="text-gray-400 text-sm">{config.hours}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-amber-400">{dp.avg_listeners?.toFixed(1)}</p>
                          <p className="text-gray-400 text-xs">μέσοι ακροατές</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </TabsContent>

              {/* Daily Tab */}
              <TabsContent value="daily" className="mt-4">
                <div className="space-y-2">
                  {analysisResult.daily?.map((day, idx) => (
                    <div key={idx} className="flex items-center gap-4 p-3 bg-gray-800/50 rounded-lg">
                      <span className="text-purple-400 font-semibold w-24">{day.date}</span>
                      <div className="flex-1 h-4 bg-gray-700 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-blue-500 to-green-500 rounded-full"
                          style={{ width: `${(day.avg_listeners / (analysisResult.summary?.max_concurrent || 1)) * 100}%` }}
                        />
                      </div>
                      <span className="text-white font-semibold w-16 text-right">{day.avg_listeners?.toFixed(1)}</span>
                      {analysisResult.analysis_type === 'combined' && day.avg_session_duration > 0 && (
                        <span className="text-amber-400 text-xs w-20 text-right">{day.avg_session_duration?.toFixed(1)} min</span>
                      )}
                    </div>
                  ))}
                </div>
              </TabsContent>

              {/* Heatmap Tab - Only for combined analysis */}
              {analysisResult.analysis_type === 'combined' && analysisResult.heatmap && (
                <TabsContent value="heatmap" className="mt-4">
                  <div className="space-y-4">
                    <p className="text-gray-400 text-sm">Χρωματική απεικόνιση ακροατών ανά ώρα και ημέρα εβδομάδας</p>
                    
                    {/* Heatmap Grid */}
                    <div className="overflow-x-auto">
                      <div className="min-w-[600px]">
                        {/* Header - Hours */}
                        <div className="flex gap-1 mb-1">
                          <div className="w-16" />
                          {Array.from({length: 24}, (_, i) => (
                            <div key={i} className="flex-1 text-center text-xs text-gray-500">
                              {i}
                            </div>
                          ))}
                        </div>
                        
                        {/* Rows - Days */}
                        {['Δευ', 'Τρί', 'Τετ', 'Πέμ', 'Παρ', 'Σάβ', 'Κυρ'].map((dayName, weekday) => {
                          const dayData = analysisResult.heatmap?.filter(h => h.weekday === weekday) || [];
                          const maxListeners = Math.max(...(analysisResult.heatmap?.map(h => h.avg_listeners) || [1]));
                          
                          return (
                            <div key={weekday} className="flex gap-1 mb-1">
                              <div className="w-16 text-xs text-gray-400 flex items-center">{dayName}</div>
                              {Array.from({length: 24}, (_, hour) => {
                                const cellData = dayData.find(d => d.hour === hour);
                                const value = cellData?.avg_listeners || 0;
                                const intensity = maxListeners > 0 ? value / maxListeners : 0;
                                
                                return (
                                  <div
                                    key={hour}
                                    className="flex-1 h-6 rounded cursor-pointer transition-all hover:ring-2 hover:ring-white/70"
                                    style={{
                                      backgroundColor: getHeatmapColor(intensity)
                                    }}
                                    title={`${dayName} ${hour}:00 - ${value.toFixed(1)} ακροατές`}
                                  />
                                );
                              })}
                            </div>
                          );
                        })}
                        
                        {/* Legend - Red to Green to Gray to Black */}
                        <div className="flex items-center gap-2 mt-4 justify-center">
                          <span className="text-xs text-gray-400">Χαμηλό</span>
                          <div className="flex gap-0.5">
                            {[0.05, 0.2, 0.4, 0.6, 0.8, 1.0].map((intensity, i) => (
                              <div 
                                key={i}
                                className="w-6 h-4 rounded"
                                style={{ backgroundColor: getHeatmapColor(intensity) }}
                              />
                            ))}
                          </div>
                          <span className="text-xs text-gray-400">Υψηλό</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </TabsContent>
              )}
            </Tabs>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default StreamingAnalytics;
